'use client';

import { useState } from 'react';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { formatCurrency } from '@instamenu/formatters';
import { Flex } from '../flex/flex';
import { Button } from '../button/button';
import { Box } from '../box/box';
import { Text } from '../text/text';
import { CurrencyInput } from '../currency-input/currency-input';
import { Toggle } from '../toggle/toggle';
import { ModalNext } from '../modal/modal';

export type TipOption = {
  type: 'PERCENTAGE' | 'AMOUNT';
  tipPercentage?: number | undefined;
  tipAmount: number;
};

export type TipValue = {
  type: 'PERCENTAGE' | 'AMOUNT' | 'CUSTOM';
  tipPercentage?: number | undefined;
  tipAmount: number;
};

export type TipSelectorProps = {
  title?: string;
  options: TipOption[];
  initialValue?: TipValue;
  showCustomOption?: boolean;
  onClose: () => void;
  onSave: (value: { tipAmount?: number; tipPercentage?: number }) => void | Promise<void>;
  saveLabel?: string;
  cancelLabel?: string;
  opened: boolean;
};

export function TipSelector({
  title = 'Add Tip',
  options,
  initialValue,
  showCustomOption = true,
  onClose,
  onSave,
  saveLabel = 'Add Tip',
  cancelLabel = 'Cancel',
  opened,
}: TipSelectorProps) {
  const [showCustomField, setShowCustomField] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [value, setValue] = useState<TipValue>(
    initialValue || {
      type: options[0].type,
      tipPercentage: options[0].tipPercentage,
      tipAmount: options[0].tipAmount,
    },
  );

  const handleSetValue = (newValue: TipValue) => {
    setValue(newValue);
    if (newValue.type !== 'CUSTOM') {
      setShowCustomField(false);
    }
  };

  const handleSetShowCustomField = () => {
    setValue({ type: 'CUSTOM', tipPercentage: 0, tipAmount: 0 });
    setShowCustomField(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      await onSave({
        tipAmount: value.type === 'CUSTOM' || value.type === 'AMOUNT' ? value.tipAmount : undefined,
        tipPercentage: value.type === 'PERCENTAGE' ? value.tipPercentage : undefined,
      });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to update tip');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <ModalNext opened={opened} onRequestClose={handleClose} containerStyle={{ padding: 25 }}>
      <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 18 }}>
        <Text
          css={{
            flex: 1,
            margin: 0,
            fontSize: 17,
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {title}
        </Text>
      </Flex>
      <Box css={{ padding: '10px 0' }}>
        <Flex
          style={{
            border: themeVars.border.default,
            borderRadius: '8px',
            justifyContent: 'space-between',
            textAlign: 'center',
          }}
        >
          {options.map((option, index) => (
            <Toggle
              key={option.tipAmount}
              onClick={() => handleSetValue(option)}
              css={{
                backgroundColor:
                  !showCustomField && value.tipAmount === option.tipAmount
                    ? palette.gray50
                    : palette.white,
                borderRight:
                  !showCustomOption && index === options.length - 1
                    ? 'none'
                    : themeVars.border.default,
                borderRadius:
                  index === 0
                    ? '8px 0 0 8px'
                    : !showCustomOption && index === options.length - 1
                      ? '0 8px 8px 0'
                      : 'none',
              }}
            >
              {option.tipPercentage !== undefined && <Text>{option.tipPercentage}%</Text>}
              <Text
                css={{
                  color: option.tipPercentage !== undefined ? palette.gray500 : palette.gray900,
                  fontWeight: 400,
                }}
              >
                {formatCurrency(option.tipAmount)}
              </Text>
            </Toggle>
          ))}
          {showCustomOption && (
            <Toggle
              id={'Custom'}
              css={{
                borderRadius: '0 8px 8px 0',
                backgroundColor: showCustomField ? palette.gray50 : palette.white,
              }}
              onClick={handleSetShowCustomField}
            >
              Custom
            </Toggle>
          )}
        </Flex>
        {showCustomOption && showCustomField ? (
          <Box css={{ paddingTop: 20 }}>
            <Text css={{ paddingBottom: '6px' }}>Enter dollar amount</Text>
            <CurrencyInput
              type="text"
              inputMode="decimal"
              onChange={(v) =>
                handleSetValue({ type: 'CUSTOM', tipPercentage: undefined, tipAmount: v })
              }
              value={value.tipAmount}
              autoFocus
            />
          </Box>
        ) : null}
      </Box>
      {saveError ? (
        <Text css={{ color: palette.warning, paddingBottom: 12 }}>{saveError}</Text>
      ) : null}
      <Flex style={{ gap: 16 }}>
        <Button variant="secondary" css={{ flex: 1 }} onClick={handleClose} disabled={saving}>
          {cancelLabel}
        </Button>
        <Button css={{ flex: 1 }} onClick={handleSave} loading={saving} disabled={saving}>
          {saveLabel}
        </Button>
      </Flex>
    </ModalNext>
  );
}
